import { Component, computed, input, output, signal } from '@angular/core';
import { FilterChange, PendingPresetChange } from '../models/preset-change.models';
import { PresetPreviewService } from '../services/preset-preview.service';
import { POE1_PROGRESS_PRESETS, POE2_PROGRESS_PRESETS } from '../services/presets.service';
import { ArrayDiffItem, buildArrayDiff, formatChangeValue, isPrimitiveArray } from '../services/filter-diff.util';
import { BaseComponent } from '../common/base.component';
import { CurrencyTieringMode, UniquesTieringMode } from '../models/enums';
import { Tier } from '../models/item-data-models';
import { CurrencyTierService } from '../services/tiering/currency-tier.service';
import { UniquesTierService } from '../services/tiering/uniques-tier.service';

const SECTION_LABELS: Record<string, string> = {
  leagueStart: 'League Start',
  currency: 'Currency',
  uniques: 'Uniques',
  other: 'Other Items',
  myEquipment: 'Favourite Equipment',
  otherEquipment: 'Other Equipment',
  remainingEquipment: 'Remaining Equipment',
};

interface TieringCutoffHint {
  priceLabel: string;
  priceBaseCurrency?: string;
  priceBaseCurrencyImage?: string;
  isCurrency: boolean;
}

interface DisplayChange extends FilterChange {
  formattedCurrentValue: string;
  formattedNewValue: string;
  arrayDiffItems: ArrayDiffItem[] | null;
  tieringCutoffHint: TieringCutoffHint | null;
}

const CURRENCY_TIERING_STRICTNESS: CurrencyTieringMode[] = ['Early', 'Normal', 'Strict', 'VeryStrict'];
const UNIQUES_TIERING_STRICTNESS: UniquesTieringMode[] = ['Normal', 'Strict', 'VeryStrict'];

interface SectionGroup {
  label: string;
  changes: DisplayChange[];
}

interface ClassPresetEntry {
  key: string;
  img: string;
}

@Component({
  selector: 'app-preset-changes-dialog',
  templateUrl: './preset-changes-dialog.component.html',
  styleUrls: ['../common/dialogs.scss', './preset-changes-dialog.component.scss'],
})
export class PresetChangesDialogComponent extends BaseComponent {
  pendingChange = input.required<PendingPresetChange>();
  confirm = output<void>();
  cancel = output<void>();

  showOnlyUserModified = signal(false);

  progressPresets = this.poe2 ? POE2_PROGRESS_PRESETS : POE1_PROGRESS_PRESETS;

  classPresets = computed<ClassPresetEntry[]>(() => {
    const classes = this.poe2
      ? ['Warrior', 'Mercenary', 'Ranger', 'Huntress', 'Monk', 'Witch', 'Sorceress', 'Druid']
      : ['Marauder', 'Duelist', 'Ranger', 'Scion', 'Shadow', 'Witch', 'Templar'];
    return classes.map(cls => ({
      key: cls,
      img: `${this.imgPrefix}/img/presets/${cls.toLowerCase()}.webp`,
    }));
  });

  totalChangeCount = computed(() => this.pendingChange().changes.length);

  userModifiedChangeCount = computed(() => this.pendingChange().changes.filter(c => c.isUserModified).length);

  changesBySection = computed<SectionGroup[]>(() => {
    const changes = this.showOnlyUserModified()
      ? this.pendingChange().changes.filter(c => c.isUserModified)
      : this.pendingChange().changes;
    const sections = new Map<string, DisplayChange[]>();
    for (const change of changes) {
      const groupLabel = change.groupLabel ?? SECTION_LABELS[change.section] ?? change.section;
      if (!sections.has(groupLabel)) sections.set(groupLabel, []);
      const arrayDiffItems = isPrimitiveArray(change.currentValue) && isPrimitiveArray(change.newValue)
        ? buildArrayDiff(change.currentValue, change.newValue)
        : null;
      sections.get(groupLabel)!.push({
        ...change,
        formattedCurrentValue: formatChangeValue(change.currentValue),
        formattedNewValue: formatChangeValue(change.newValue),
        arrayDiffItems,
        tieringCutoffHint: this.buildTieringCutoffHint(change),
      });
    }
    return Array.from(sections.entries()).map(([label, ch]) => ({ label, changes: ch }));
  });

  isRevertingCurrentPreset = computed(() => {
    const pending = this.pendingChange();
    return !!pending.originalPreset && pending.newPreset === pending.originalPreset;
  });

  constructor(
    private presetPreviewService: PresetPreviewService,
    private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService,
  ) {
    super();
  }

  private buildTieringCutoffHint(change: FilterChange): TieringCutoffHint | null {
    const targetFilter = this.pendingChange().targetFilter;

    if (change.key === 'tieringMode') {
      const from = change.currentValue as CurrencyTieringMode;
      const to = change.newValue as CurrencyTieringMode;
      if (!this.isStricterTieringMode(CURRENCY_TIERING_STRICTNESS, from, to)) return null;
      const display = this.currencyTierService.getDisplayValueForMode(to, Tier.FHidden, targetFilter);
      if (!display) return null;
      return { ...display, isCurrency: true };
    }

    if (change.key === 'uniquesTieringMode') {
      const from = change.currentValue as UniquesTieringMode;
      const to = change.newValue as UniquesTieringMode;
      if (!this.isStricterTieringMode(UNIQUES_TIERING_STRICTNESS, from, to)) return null;
      const display = this.uniquesTierService.getDisplayValueForMode(to, 'Other', targetFilter);
      if (!display) return null;
      return { ...display, isCurrency: false };
    }

    return null;
  }

  private isStricterTieringMode<T extends string>(order: T[], from: T, to: T): boolean {
    if (from === 'Custom' || to === 'Custom') return false;
    const fromIndex = order.indexOf(from);
    const toIndex = order.indexOf(to);
    if (fromIndex === -1 || toIndex === -1) return false;
    return toIndex > fromIndex;
  }

  setPreviewPreset(preset: string): void {
    this.presetPreviewService.updatePreviewPreset(preset);
  }

  onConfirm(): void {
    this.confirm.emit();
  }

  onCancel(): void {
    this.cancel.emit();
  }

  disableAutoPreview(event: Event): void {
    event.preventDefault();
    this.settings.setAutoPreviewPresetChanges(false);
  }
}
